import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { Role } from '../../services/session/user.model';
import { AppAbility } from './app-ability';

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  SINGLE SOURCE OF TRUTH FOR ROLE → PERMISSION MAPPING                  │
 * │                                                                         │
 * │  To enable a feature for a role:                                        │
 * │    can('read', 'JobList')   ← add inside the role's case block         │
 * │                                                                         │
 * │  To disable a feature that was inherited:                               │
 * │    cannot('delete', 'Tables')  ← CASL respects last-rule-wins          │
 * └─────────────────────────────────────────────────────────────────────────┘
 */
export function defineAbilityFor(role: Role | undefined): AppAbility {
    const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

    switch (role) {
    // ── ROOT_ADMIN ─────────────────────────────────────────────────────
    case 'ROOT_ADMIN':
        can('manage', 'all');       // unrestricted access to everything
        break;

        // ── SYSTEM_ADMIN / ACCOUNT_ADMIN ──────────────────────────────────
    case 'SYSTEM_ADMIN':
    case 'ACCOUNT_ADMIN':
        can('read',   'SystemAdmin');
        can('create', 'SystemAdmin');   // create organisations
        can('delete', 'SystemAdmin');   // delete organisations
        can('read',   'Permissions');
        can('manage', 'ApiKeys');
        break;

        // ── ORG_ADMIN ──────────────────────────────────────────────────────
    case 'ORG_ADMIN':
        // pages
        can('read', ['Tables', 'Files', 'Directories', 'Schemas', 'ImportAttempts',
            'LocationKeys', 'Users', 'Connectors', 'Integrations',
            'Models', 'Map', 'Permissions',
            'OrganizationAdmin', 'Workspaces']);
        // features
        can('manage', 'TableMutations');   // create + delete tables
        can('manage', 'UserManagement');   // invite + remove users
        can('read',   'JobList');
        can('read',   'OrgSwitcher');
        break;

        // ── ORG_MEMBER ─────────────────────────────────────────────────────
    case 'ORG_MEMBER':
        // pages (read-only subset)
        can('read', ['Tables', 'Files', 'Directories', 'Schemas', 'Workspaces']);
        // features
        can('read', 'OrgSwitcher');
        break;

    default:
        break;
    }

    return build();
}
